import { RowData, getAllDescendants, getAllAncestors, getParentId } from "./selectionUtils";

/**
 * Updates parent selection states based on children selection
 */
const updateParentSelectionStates = (
  allRows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>
): { newSelectedSet: Set<any>; newDeselectedSet: Set<any> } => {
  const newSelectedSet = new Set(selectedRowIds);
  const newDeselectedSet = new Set(explicitlyDeselectedIds);

  // Get all potential parents
  const parentsWithChildren = allRows.filter((row) =>
    allRows.some((child) => getParentId(child) === row.id)
  );

  // Update all levels recursively until no more changes
  let changed = true;
  let iterations = 0;
  const maxIterations = 10; // Prevent infinite loops

  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;
    for (const parent of parentsWithChildren) {
      const children = allRows.filter((child) => getParentId(child) === parent.id);
      if (children.length > 0) {
        const result = updateSingleParentState(
          parent,
          children,
          allRows,
          newSelectedSet,
          newDeselectedSet
        );

        if (result.hasChanged) {
          changed = true;
        }
      }
    }
  }

  return { newSelectedSet, newDeselectedSet };
};

/**
 * Updates the selection state for a single parent based on its children
 */
const updateSingleParentState = (
  parent: RowData,
  children: RowData[],
  allRows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>
): { hasChanged: boolean } => {
  let hasChanged = false;

  // Calculate effective selection state for each child
  const effectivelySelectedChildren = children.filter((child) => {
    // Child is effectively selected if:
    // 1. Not explicitly deselected AND
    // 2. (Directly selected OR inherits from any selected ancestor)
    if (explicitlyDeselectedIds.has(child.id)) return false;
    if (selectedRowIds.has(child.id)) return true;

    const childAncestors = getAllAncestors(child.id, allRows);
    return childAncestors.some((ancestorId) => selectedRowIds.has(ancestorId));
  });

  // Check if any child has indeterminate state
  const hasIndeterminateChildren = checkForIndeterminateChildren(
    children,
    allRows,
    selectedRowIds,
    explicitlyDeselectedIds
  );

  const selectionAnalysis = analyzeChildrenSelection(
    children,
    effectivelySelectedChildren,
    hasIndeterminateChildren,
    selectedRowIds
  );

  // Update parent based on children state
  if (selectionAnalysis.shouldSelectParent && !selectedRowIds.has(parent.id)) {
    selectedRowIds.add(parent.id);
    explicitlyDeselectedIds.delete(parent.id);
    hasChanged = true;
  } else if (selectionAnalysis.shouldDeselectParent && selectedRowIds.has(parent.id)) {
    handleParentDeselection(parent, allRows, selectedRowIds, explicitlyDeselectedIds);
    hasChanged = true;
  } else if (selectionAnalysis.shouldMakeIndeterminate && selectedRowIds.has(parent.id)) {
    handleParentIndeterminate(parent, children, allRows, selectedRowIds, explicitlyDeselectedIds);
    hasChanged = true;
  }

  return { hasChanged };
};

/**
 * Check if any children have indeterminate state
 */
const checkForIndeterminateChildren = (
  children: RowData[],
  allRows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>
): boolean => {
  return children.some((child) => {
    // Get child's direct descendants to check their selection state
    const childDescendants = getAllDescendants(child.id, allRows);

    if (childDescendants.length === 0) {
      // Leaf node - can't be indeterminate
      return false;
    }

    // Count effectively selected descendants - this is independent of whether child is explicitly deselected
    let effectivelySelectedDescendants = 0;
    let totalDescendants = childDescendants.length;
    for (const descendantId of childDescendants) {
      if (explicitlyDeselectedIds.has(descendantId)) {
        // Explicitly deselected - not counted as selected
        return;
      }

      if (selectedRowIds.has(descendantId)) {
        // Directly selected
        effectivelySelectedDescendants++;
        return;
      }

      // Check if inherited from an ancestor (but not if that ancestor is also explicitly deselected)
      const descendantAncestors = getAllAncestors(descendantId, allRows);
      const hasSelectedAncestor = descendantAncestors.some((ancestorId) => {
        return selectedRowIds.has(ancestorId) && !explicitlyDeselectedIds.has(ancestorId);
      });

      if (hasSelectedAncestor) {
        effectivelySelectedDescendants++;
      }
    }

    // Child is indeterminate if some but not all descendants are selected
    // This is independent of whether the child itself is explicitly deselected
    const isChildIndeterminate =
      effectivelySelectedDescendants > 0 && effectivelySelectedDescendants < totalDescendants;

    return isChildIndeterminate;
  });
};

/**
 * Analyze children selection to determine parent action
 */
const analyzeChildrenSelection = (
  children: RowData[],
  effectivelySelectedChildren: RowData[],
  hasIndeterminateChildren: boolean,
  selectedRowIds: Set<any>
) => {
  const allChildrenEffectivelySelected =
    effectivelySelectedChildren.length === children.length && children.length > 0;
  const allChildrenDirectlySelected =
    children.every((child) => selectedRowIds.has(child.id)) && children.length > 0;

  // A parent should only be fully selected if ALL children are directly selected AND no children are indeterminate
  const allChildrenSelected =
    allChildrenEffectivelySelected && allChildrenDirectlySelected && !hasIndeterminateChildren;

  // A parent should only be deselected if NO children are selected AND none are indeterminate
  const noChildrenSelected = effectivelySelectedChildren.length === 0 && !hasIndeterminateChildren;

  // A parent should be indeterminate if some (but not all) children are selected, OR if any child is indeterminate
  const someChildrenSelected =
    (effectivelySelectedChildren.length > 0 &&
      effectivelySelectedChildren.length < children.length) ||
    hasIndeterminateChildren ||
    (effectivelySelectedChildren.length > 0 && !allChildrenDirectlySelected);

  return {
    shouldSelectParent: allChildrenSelected,
    shouldDeselectParent: noChildrenSelected,
    shouldMakeIndeterminate: someChildrenSelected,
  };
};

/**
 * Handle parent deselection logic
 */
const handleParentDeselection = (
  parent: RowData,
  allRows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>
) => {
  const parentAncestors = getAllAncestors(parent.id, allRows);
  const hasSelectedParentAncestor = parentAncestors.some((ancestorId) =>
    selectedRowIds.has(ancestorId)
  );

  if (hasSelectedParentAncestor) {
    explicitlyDeselectedIds.add(parent.id);
  } else {
    selectedRowIds.delete(parent.id);
  }

  // When removing a parent, also remove descendants that were only selected through inheritance
  const descendants = getAllDescendants(parent.id, allRows);
  for (const descendantId of descendants) {
    // Only remove descendants that don't have other selected ancestors
    const descendantAncestors = getAllAncestors(descendantId, allRows);
    const hasOtherSelectedAncestor = descendantAncestors.some(
      (ancestorId) => ancestorId !== parent.id && selectedRowIds.has(ancestorId)
    );

    if (!hasOtherSelectedAncestor) {
      selectedRowIds.delete(descendantId);
      explicitlyDeselectedIds.delete(descendantId);
    }
  }
};

/**
 * Handle parent indeterminate state logic
 */
const handleParentIndeterminate = (
  parent: RowData,
  children: RowData[],
  allRows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>
) => {
  // Explicitly select the children that should remain selected
  const childrenToKeepSelected = children.filter((child) => {
    return !explicitlyDeselectedIds.has(child.id);
  });

  for (const child of childrenToKeepSelected) {
    // Add remaining children to selected set
    selectedRowIds.add(child.id);
  }

  // Remove parent from selected
  const parentAncestors = getAllAncestors(parent.id, allRows);
  const hasSelectedParentAncestor = parentAncestors.some((ancestorId) =>
    selectedRowIds.has(ancestorId)
  );

  if (hasSelectedParentAncestor) {
    explicitlyDeselectedIds.add(parent.id);
  } else {
    selectedRowIds.delete(parent.id);
  }
};

// Keep track of pending updates to cancel them if needed
let pendingUpdateId: NodeJS.Timeout | null = null;

/**
 * Cancel any pending hierarchical updates
 */
export const cancelPendingUpdates = () => {
  if (pendingUpdateId !== null) {
    clearTimeout(pendingUpdateId);
    pendingUpdateId = null;
  }
};

/**
 * Main function to handle hierarchical selection updates
 */
export const updateHierarchicalSelection = (
  rowId: any,
  selected: boolean,
  allRows: RowData[],
  selectedRowIds: Set<any>,
  explicitlyDeselectedIds: Set<any>,
  onSelectionUpdate: (selectedIds: Set<any>, deselectedIds: Set<any>) => void
) => {
  // Cancel any pending updates to prevent conflicts
  cancelPendingUpdates();

  // Handle immediate selection/deselection
  const newSelectedSet = new Set(selectedRowIds);
  const newDeselectedSet = new Set(explicitlyDeselectedIds);

  if (selected) {
    // When selecting: add this row to selected
    newSelectedSet.add(rowId);
    // Remove this row and descendants from explicitly deselected
    newDeselectedSet.delete(rowId);
    const descendants = getAllDescendants(rowId, allRows);
    for (const id of descendants) {
      newDeselectedSet.delete(id);
    }
  } else {
    // When deselecting: remove this row and all its descendants from selected
    newSelectedSet.delete(rowId);
    const descendants = getAllDescendants(rowId, allRows);
    for (const id of descendants) {
      newSelectedSet.delete(id);
    }

    // Check if any ancestor is selected
    const ancestors = getAllAncestors(rowId, allRows);
    const hasSelectedAncestor = ancestors.some((ancestorId) => selectedRowIds.has(ancestorId));

    if (hasSelectedAncestor) {
      newDeselectedSet.add(rowId);
    }

    // When deselecting a parent, remove descendants from explicitly deselected
    for (const id of descendants) {
      newDeselectedSet.delete(id);
    }
  }

  // First update with immediate changes
  onSelectionUpdate(newSelectedSet, newDeselectedSet);

  // Then update ancestors with cancellation tracking
  pendingUpdateId = setTimeout(() => {
    // Only proceed if this update hasn't been cancelled
    if (pendingUpdateId !== null) {
      const finalResult = updateParentSelectionStates(allRows, newSelectedSet, newDeselectedSet);
      onSelectionUpdate(finalResult.newSelectedSet, finalResult.newDeselectedSet);
      pendingUpdateId = null;
    }
  }, 0);
};

export default updateHierarchicalSelection;
export { updateParentSelectionStates };
